// src/components/AgendaGrid.jsx
import React from 'react';
import AgendaSlot from './AgendaSlot';
import { etiquetaMotivoBloqueo } from '../hooks/useBloqueos';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

/**
 * ============================================================================
 * REJILLA CENTRAL DE AGENDAS (MATRIZ DE HORAS POR MÉDICO) - TAILWIND VERSION
 * ============================================================================
 */
const AgendaGrid = ({
                        medicosVisibles,
                        horasLaborables,
                        obtenerCitaEspecifica,
                        fecha,
                        pacienteSeleccionadoBusqueda,
                        onAbrirEstacion,
                        onContextMenu,
                        onVerExpediente,
                        onCobrarConsulta,
                        onCancelarCita,
                        onMarcarAusente,
                        obtenerBloqueo,
                        puedeQuitarBloqueo,
                        onQuitarBloqueo
                    }) => {

    // Calculamos el ancho mínimo dinámico y las columnas de la rejilla
    const gridStyle = {
        gridTemplateColumns: `80px repeat(${medicosVisibles.length}, minmax(320px, 1fr))`,
        minWidth: medicosVisibles.length > 0 ? `calc(80px + ${medicosVisibles.length * 320}px)` : '100%'
    };

    return (
        <div
            style={gridStyle}
            className="grid border-t border-l border-slate-200 rounded-t-2xl overflow-hidden bg-white shadow-sm"
        >
            {/* CABECERA Sticky: Etiqueta "HORA" */}
            <div className="sticky top-0 left-0 z-40 bg-slate-50 border-b-2 border-r-2 border-slate-200 flex items-center justify-center py-3">
                <span className="text-[10px] font-black tracking-wider text-slate-500 uppercase">
                    Hora
                </span>
            </div>

            {/* CABECERA Sticky: Columnas de Médicos */}
            {medicosVisibles.map((m) => (
                <div
                    key={m.id}
                    className="sticky top-0 z-30 bg-slate-50 border-b-2 border-slate-200 flex items-center gap-3 px-5 py-2.5 backdrop-blur-md bg-opacity-95"
                >
                    {/* Avatar Nativo con Iniciales */}
                    <div className="w-9 h-9 rounded-full bg-slate-950 text-white font-extrabold text-xs flex items-center justify-center shadow-sm shrink-0">
                        {m.nombre?.[0]}{m.apellidos?.[0]}
                    </div>
                    <div className="overflow-hidden">
                        <h3 className="text-sm font-black text-slate-900 truncate leading-tight">
                            {m.nombre} {m.apellidos}
                        </h3>
                        <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wide block mt-0.5">
                            {m.especialidad || 'General'}
                        </span>
                    </div>
                </div>
            ))}

            {/* FILAS DE TIEMPO (MATRIZ DINÁMICA DE TRABAJO) */}
            {horasLaborables.map((hora, indiceHora) => (
                <React.Fragment key={hora}>
                    {/* Celda de guía horaria lateral izquierda */}
                    <div className="sticky left-0 z-20 bg-slate-50 border-r-2 border-b border-slate-200 flex items-center justify-center font-black text-xs text-slate-600 shadow-[2px_0_5px_rgba(0,0,0,0.02)]">
                        {hora}
                    </div>

                    {/* Celdas cruzadas (Slots asignables o citas arrastrables) */}
                    {medicosVisibles.map((medico) => {
                        const cita = obtenerCitaEspecifica(hora, medico.id);
                        const bloqueo = !cita && obtenerBloqueo ? obtenerBloqueo(hora, medico.id) : null;

                        if (bloqueo) {
                            // Solo el primer tramo del bloqueo lleva el motivo y el botón de quitar
                            const anterior = indiceHora > 0 ? obtenerBloqueo(horasLaborables[indiceHora - 1], medico.id) : null;
                            return (
                                <div
                                    key={`${hora}-${medico.id}`}
                                    className="border-b border-r border-slate-200 h-[130px] flex items-stretch min-w-0 overflow-hidden p-0.5"
                                >
                                    <FranjaBloqueo
                                        bloqueo={bloqueo}
                                        esInicio={!anterior || anterior.id !== bloqueo.id}
                                        puedeQuitar={puedeQuitarBloqueo ? puedeQuitarBloqueo(bloqueo) : false}
                                        onQuitar={onQuitarBloqueo}
                                    />
                                </div>
                            );
                        }

                        return (
                            <div
                                key={`${hora}-${medico.id}`}
                                className="border-b border-r border-slate-200 h-[130px] flex items-stretch bg-transparent min-w-0 overflow-hidden p-0.5"
                            >
                                <AgendaSlot
                                    hora={hora}
                                    medicoId={medico.id}
                                    cita={cita}
                                    fechaBase={fecha}
                                    pacienteSeleccionado={pacienteSeleccionadoBusqueda}
                                    onAbrirEstacion={onAbrirEstacion}
                                    onContextMenu={onContextMenu}
                                    onVerExpediente={onVerExpediente}
                                    onCobrarConsulta={onCobrarConsulta}
                                    onCancelarCita={onCancelarCita}
                                    onMarcarAusente={onMarcarAusente}
                                />
                            </div>
                        );
                    })}
                </React.Fragment>
            ))}
        </div>
    );
};

// Tramo gris de un bloqueo de agenda (vacaciones, festivo, formación…)
function FranjaBloqueo({ bloqueo, esInicio, puedeQuitar, onQuitar }) {
    return (
        <div
            className="w-full rounded-xl bg-slate-100 border border-slate-200 flex flex-col justify-between p-3 text-slate-500"
            style={{ backgroundImage: 'repeating-linear-gradient(135deg, transparent 0 10px, rgba(148,163,184,0.12) 10px 20px)' }}
            title={etiquetaMotivoBloqueo(bloqueo)}
        >
            {esInicio ? (
                <>
                    <div className="flex items-start gap-2 min-w-0">
                        <EventBusyOutlinedIcon sx={{ fontSize: 18 }} className="shrink-0 mt-0.5" />
                        <div className="min-w-0">
                            <span className="block text-xs font-black uppercase tracking-wide text-slate-600 truncate">
                                {etiquetaMotivoBloqueo(bloqueo)}
                            </span>
                            <span className="block text-[10px] font-bold text-slate-400 mt-0.5">
                                {bloqueo.profesional ? 'Agenda cerrada' : 'Toda la clínica'}
                            </span>
                        </div>
                    </div>
                    {puedeQuitar && (
                        <button
                            type="button"
                            onClick={() => onQuitar && onQuitar(bloqueo)}
                            className="self-start inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold text-slate-500 hover:text-rose-600 hover:bg-white transition-colors"
                        >
                            <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                            Quitar bloqueo
                        </button>
                    )}
                </>
            ) : (
                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Bloqueado</span>
            )}
        </div>
    );
}

export default AgendaGrid;